import React from 'react';
import numeral from 'numeral';

// 表示可能な列の定義（メインテーブル・日次KPIモーダル共通）
export const columnDefinitions = [
  { key: 'grossSales', label: '総売上', sortable: true, minWidth: 100 },
  { key: 'refund', label: '売上返金', sortable: true, minWidth: 100 },
  { key: 'vineSales', label: 'Vine売上', sortable: true, minWidth: 100 },
  { key: 'sales', label: '売上', sortable: true, minWidth: 100 },
  { key: 'grossQuantity', label: '総販売数', sortable: true, minWidth: 80 },
  { key: 'refundQuantity', label: '返品数', sortable: true, minWidth: 80 },
  { key: 'vineQuantity', label: 'Vine販売数', sortable: true, minWidth: 80 },
  { key: 'quantity', label: '販売数', sortable: true, minWidth: 80 },
  { key: 'cost', label: '原価', sortable: false, minWidth: 100 },
  { key: 'salesFees', label: '販売手数料', sortable: false, minWidth: 100 },
  { key: 'systemUsageFee', label: 'システム利用料', sortable: false, minWidth: 120 },
  { key: 'safetyFee', label: '安全性利用料', sortable: false, minWidth: 110 },
  { key: 'paymentFee', label: '決済手数料', sortable: false, minWidth: 100 },
  { key: 'pointsBaseCost', label: 'ポイント原資', sortable: false, minWidth: 110 },
  { key: 'fbaShippingCost', label: 'FBA配送料', sortable: false, minWidth: 100 },
  { key: 'shippingCost', label: '配送料', sortable: false, minWidth: 100 },
  { key: 'grossProfit', label: '純粗利益', sortable: true, minWidth: 100 },
  { key: 'grossProfitRate', label: '純粗利益率', sortable: true, minWidth: 100 },
  { key: 'adCost', label: '広告費', sortable: true, minWidth: 100 },
  { key: 'promotionCost', label: 'プロモ割引等', sortable: true, minWidth: 100 },
  { key: 'pointsCost', label: 'ポイント', sortable: true, minWidth: 100 },
  { key: 'contributionMargin', label: '限界利益', sortable: true, minWidth: 100 },
  { key: 'contributionMarginRate', label: '限界利益率', sortable: true, minWidth: 100 },
  { key: 'adSales', label: '広告売上', sortable: true, minWidth: 100 },
  { key: 'adSalesRatio', label: '広告売上比率', sortable: true, minWidth: 100 },
  { key: 'organicSales', label: 'オーガニック売上', sortable: true, minWidth: 100 },
  { key: 'organicSalesRatio', label: 'オーガニック売上比率', sortable: true, minWidth: 100 },
  { key: 'acos', label: 'ACOS', sortable: true, minWidth: 100 },
  { key: 'acots', label: 'TACOS', sortable: true, minWidth: 100 },
  { key: 'fbaQuantity', label: 'FBA在庫数', sortable: true, minWidth: 80 },
  { key: 'fbaInventoryAmount', label: 'FBA在庫額', sortable: true, minWidth: 100 },
  { key: 'dailyAverageQuantity', label: '平均販売数/日', sortable: true, minWidth: 100 },
  { key: 'daysUntilStockout', label: '在庫切れ予測', sortable: true, minWidth: 100, help: '現在のFBA在庫数を平均販売数/日で割って算出した、在庫がなくなるまでの予測日数です（FBA在庫数 ÷ 平均販売数/日）。平均販売数/日が0の場合は「-」と表示されます。' },
];

export const defaultVisibleColumns = columnDefinitions.map(_ => _.key);

// 在庫スナップショット系・予測系は日次表示に馴染まないため日次KPIでは除外
const dailyExcludedColumnKeys = ['fbaQuantity', 'fbaInventoryAmount', 'dailyAverageQuantity', 'daysUntilStockout'];
export const dailyColumnKeys = columnDefinitions.map(_ => _.key).filter(_ => !dailyExcludedColumnKeys.includes(_));

export function NumberTd({ value, format = '0,0', salesRatio, isCurrency = false, bottomContent }) {
  const formattedValue = numeral(value).format(format);
  const displayValue = isCurrency && !format.includes('%') ? `${formattedValue}円` : formattedValue;
  const ratioDisplay = salesRatio ? `(${numeral(salesRatio).format('0.0%')})` : '';

  return (
    <td className="text-end">
      {displayValue}
      {ratioDisplay && <div className="text-muted small">{ratioDisplay}</div>}
      {bottomContent}
    </td>
  );
}

export function renderKpiCells(kpis, unitCost, unitShippingCost, visibleColumns) {
  const cellRenderers = {
    grossSales: () => <NumberTd key="grossSales" value={kpis.grossSales} isCurrency />,
    refund: () => <NumberTd key="refund" value={Math.abs(kpis.refund)} isCurrency />,
    vineSales: () => <NumberTd key="vineSales" value={kpis.vineSales || 0} isCurrency />,
    sales: () => <NumberTd key="sales" value={kpis.sales} isCurrency />,
    grossQuantity: () => <NumberTd key="grossQuantity" value={kpis.grossQuantity} format="0,0" />,
    refundQuantity: () => <NumberTd key="refundQuantity" value={kpis.refundQuantity || 0} format="0,0" />,
    vineQuantity: () => <NumberTd key="vineQuantity" value={kpis.vineQuantity || 0} format="0,0" />,
    quantity: () => <NumberTd key="quantity" value={kpis.quantity} format="0,0" />,
    cost: () => (
      <NumberTd
        key="cost"
        value={kpis.cost}
        salesRatio={kpis.sales + kpis.refund > 0 ? kpis.cost / (kpis.sales + kpis.refund) : 0}
        isCurrency
        bottomContent={unitCost ? <div className="text-muted small">@{numeral(unitCost).format('0,0')}円</div> : null}
      />
    ),
    salesFees: () => <NumberTd key="salesFees" value={Math.abs(kpis.salesFees)} salesRatio={kpis.sales > 0 ? Math.abs(kpis.salesFees) / kpis.sales : 0} isCurrency />,
    systemUsageFee: () => <NumberTd key="systemUsageFee" value={kpis.systemUsageFee || 0} salesRatio={kpis.sales > 0 ? (kpis.systemUsageFee || 0) / kpis.sales : 0} isCurrency />,
    safetyFee: () => <NumberTd key="safetyFee" value={kpis.safetyFee || 0} salesRatio={kpis.sales > 0 ? (kpis.safetyFee || 0) / kpis.sales : 0} isCurrency />,
    paymentFee: () => <NumberTd key="paymentFee" value={kpis.paymentFee || 0} salesRatio={kpis.sales > 0 ? (kpis.paymentFee || 0) / kpis.sales : 0} isCurrency />,
    pointsBaseCost: () => <NumberTd key="pointsBaseCost" value={Math.abs(kpis.pointsBaseCost || 0)} salesRatio={kpis.sales > 0 ? Math.abs(kpis.pointsBaseCost || 0) / kpis.sales : 0} isCurrency />,
    fbaShippingCost: () => <NumberTd key="fbaShippingCost" value={kpis.fbaShippingCost} salesRatio={kpis.sales > 0 ? kpis.fbaShippingCost / kpis.sales : 0} isCurrency />,
    shippingCost: () => (
      <NumberTd
        key="shippingCost"
        value={kpis.shippingCost}
        salesRatio={kpis.sales > 0 ? kpis.shippingCost / kpis.sales : 0}
        isCurrency
        bottomContent={unitShippingCost ? <div className="text-muted small">@{numeral(unitShippingCost).format('0,0')}円</div> : null}
      />
    ),
    grossProfit: () => (
      <NumberTd
        key="grossProfit"
        value={kpis.grossProfit}
        isCurrency
        bottomContent={kpis.quantity > 0 ? <div className="text-muted small">@{numeral(Math.round(kpis.grossProfit / kpis.quantity)).format('0,0')}円</div> : null}
      />
    ),
    grossProfitRate: () => <NumberTd key="grossProfitRate" value={kpis.grossProfitRate} format="0.0%" />,
    adCost: () => <NumberTd key="adCost" value={kpis.adCost} salesRatio={kpis.sales > 0 ? kpis.adCost / kpis.sales : 0} isCurrency />,
    promotionCost: () => <NumberTd key="promotionCost" value={Math.abs(kpis.promotionCost || 0)} salesRatio={kpis.sales > 0 ? Math.abs(kpis.promotionCost || 0) / kpis.sales : 0} isCurrency />,
    pointsCost: () => <NumberTd key="pointsCost" value={Math.abs(kpis.pointsCost || 0)} salesRatio={kpis.sales > 0 ? Math.abs(kpis.pointsCost || 0) / kpis.sales : 0} isCurrency />,
    contributionMargin: () => (
      <NumberTd
        key="contributionMargin"
        value={kpis.contributionMargin}
        isCurrency
        bottomContent={kpis.quantity > 0 ? <div className="text-muted small">@{numeral(Math.round(kpis.contributionMargin / kpis.quantity)).format('0,0')}円</div> : null}
      />
    ),
    contributionMarginRate: () => <NumberTd key="contributionMarginRate" value={kpis.contributionMarginRate} format="0.0%" />,
    adSales: () => <NumberTd key="adSales" value={kpis.adSales} isCurrency />,
    adSalesRatio: () => <NumberTd key="adSalesRatio" value={kpis.adSalesRatio} format="0.0%" />,
    organicSales: () => <NumberTd key="organicSales" value={kpis.organicSales} isCurrency />,
    organicSalesRatio: () => <NumberTd key="organicSalesRatio" value={kpis.organicSalesRatio} format="0.0%" />,
    acos: () => <NumberTd key="acos" value={kpis.acos} format="0.0%" />,
    acots: () => <NumberTd key="acots" value={kpis.acots} format="0.0%" />,
    fbaQuantity: () => <NumberTd key="fbaQuantity" value={kpis.fbaQuantity} format="0,0" />,
    fbaInventoryAmount: () => <NumberTd key="fbaInventoryAmount" value={kpis.fbaInventoryAmount} isCurrency />,
    dailyAverageQuantity: () => <NumberTd key="dailyAverageQuantity" value={kpis.dailyAverageQuantity || 0} format="0.0" />,
    daysUntilStockout: () => (
      <td key="daysUntilStockout" className="text-end">
        {kpis.daysUntilStockout == null ? '-' : `${numeral(kpis.daysUntilStockout).format('0.0')}日`}
      </td>
    ),
  };

  return columnDefinitions.filter(_ => visibleColumns.includes(_.key)).map(_ => cellRenderers[_.key]());
}
